import { Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { UntilDestroy } from "@ngneat/until-destroy";
import { GroupService } from "@services/groups/group.service";
import { IGroupWithIds } from "../_models/IGroupWithIds";
import "./group-hierarchy.component.scss";

@UntilDestroy()
@Component({
    selector: "group-hierarchy2",
    template: require("./group-hierarchy.component.html")
})
export class GroupHierarchyComponent2 implements OnInit, OnChanges {
    @Input() selectedGroup: IGroupWithIds;
    @Input() searchString: string;
    @Input() groups: IGroupWithIds[] = [];
    @Input() autoLoadGroups = false;
    @Input() showCounts = false;
    @Input() multiSelect: boolean;
    @Input() showFilter: boolean;

    @HostBinding("class.readonly") @Input() readOnly = false;

    @Output() groupSelected = new EventEmitter<IGroupWithIds>();
    @Output() groupsReady = new EventEmitter<IGroupWithIds[]>();

    loading = true;

    filterTabs: IFilterBarTab<IGroupWithIds>[];

    displayHierarchy: IGroupWithIds[] = [];

    constructor(
        private readonly filterBarService: FilterBarService,
        private readonly groupService: GroupService
    ) { }

    ngOnInit(): void {
        this.createTabs();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['groups']) {
            this.processGroups();

            if (changes['groups'].currentValue) {
                this.loading = false;
            }
        }

        if (this.autoLoadGroups && !this.searchString) {
            this.loadGroups();
        }

        if (changes["searchString"]) {
           this.search();
        }
    }

    onGroupClick(group: IGroupWithIds): void {
        if (this.multiSelect) {
            this.setGroupSelectionStatus(group, !group.selected);
            return;
        }

        this.groupSelected.emit(group);
        this.selectedGroup = group;
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<IGroupWithIds>): void {
        if (!filterEvent.filteredData) {
            return;
        }

        const groupsMapped = filterEvent.filteredData.map(group => {
            if (!group.ParentId) {
                return group;
            }

            const parent = filterEvent.filteredData.find(d => d.Id === group.ParentId);

            if (!parent) {
                // this is the case when in filtered data is a group that has
                // a parent but the parent is not present on filtered dataset.
                // in that case we treat that group as a parent.
                return { ...group, ParentId: null };
            }

            return group;
        });
                   
        this.displayHierarchy = this.groupService.buildGroupHierarchy(groupsMapped); 
    }

    groupSearchPredicate(g: IGroupWithIds, text: string): boolean {
        return g.Name.toLocaleLowerCase().indexOf(text) >= 0;
    }

    private async loadGroups(): Promise<void> {
        this.loading = true;

        this.groups = await this.groupService.getGroupsWithIds();
        this.processGroups();

        this.loading = false;
        this.groupsReady.emit(this.groups);
    }

    private processGroups(): void {
        if (!this.groups || !this.groups.length) {
            return;
        }

        this.displayHierarchy = this.groupService.buildGroupHierarchy(this.groups);
    }

    private search(): void {
        if (!this.groups || !this.groups.length) {
            return;
        }

        if (!this.searchString) {
            this.showAllGroups();
            return;
        }

        this.groups.forEach(group => {
            group.hide = false;
        });

        const lowercaseSearchString = this.searchString.toLocaleLowerCase();

        this.groups.forEach(group => {
            if (!this.doesGroupNameContainSearchParameter(group, lowercaseSearchString)) {
                group.hide = true;
            }
        });

        this.displayHierarchy = this.groupService.buildGroupHierarchy(this.groups); 
    }
    
    private showAllGroups(): void {
        if (!this.groups || !this.groups.length) {
            return;
        }

        this.groups.forEach(group => {
            group.hide = false;
        });
    }

    private doesGroupNameContainSearchParameter(group: IGroupWithIds, searchString: string, shallowCheck = false): boolean {
        group.open = true;

        if (group.Name.toLocaleLowerCase().includes(searchString)) {
            return true;
        }

        if (!shallowCheck) {
            if (group.children) {
                for (let i = 0; i < group.children.length; i++) {
                    const childGroup = group.children[i];

                    if (this.doesGroupNameContainSearchParameter(childGroup, searchString)) {
                        return true;
                    }
                }
            }

            if (group.parent && this.doesGroupNameContainSearchParameter(group.parent, searchString, true)) {
                return true;
            }
        }

        group.open = false;

        return false;
    }

    private setGroupSelectionStatus(group: IGroupWithIds, targetState: boolean): void {
        group.selected = group.parent?.selected || targetState;

        // Ensure all references have same status
        const otherGroupReference = this.groups.find(x => x.Id === group.Id);
        
        if (otherGroupReference) {
            otherGroupReference.selected = targetState;
        }

        this.groupSelected.emit(group);

        if (group.children) {
            group.children.forEach(child => {
                this.setGroupSelectionStatus(child, targetState);
            });
        }
    }
    
    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IGroupWithIds>("Common.All", () => true));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IGroupWithIds>("groups.Depots", (group) => group.IsDepot));
    }
}
